import React from 'react';
import styled, { keyframes } from 'styled-components';

import Imgix from 'shared/components/imgix';
import useTranslation from 'hooks/use-translation';

import { palette } from './palette';
import { Container, RevealOnScroll, Section, SectionHead } from './primitives';

const CATEGORIES = [
  { key: `flower`, label: `Flower`, image: `https://images.dutchie.com/home-redesign/category-flower.png` },
  { key: `edibles`, label: `Edibles`, image: `https://images.dutchie.com/home-redesign/category-edibles.png` },
  { key: `pre-rolls`, label: `Pre-Rolls`, image: `https://images.dutchie.com/home-redesign/category-pre-rolls.png` },
  {
    key: `concentrates`,
    label: `Concentrates`,
    image: `https://images.dutchie.com/home-redesign/category-concentrates.png`,
  },
  { key: `vapes`, label: `Vapes`, image: `https://images.dutchie.com/home-redesign/category-vapes.png` },
  { key: `topicals`, label: `Topicals`, image: `https://images.dutchie.com/home-redesign/category-topicals.png` },
];

export function CategoriesSection(): JSX.Element {
  const { t } = useTranslation();

  return (
    <Section>
      <Container>
        <SectionHead
          first={t('home.redesign.exploreFirst', 'Explore')}
          rest={t('home.redesign.exploreRest', 'every category')}
          sub={t('home.redesign.exploreSub', 'Find dispensaries with the best selection.')}
        />
      </Container>
      <RevealOnScroll>
        <Viewport>
          <Track>
            {CATEGORIES.map((category) => (
              <CategoryCard key={category.key}>
                <ImageBox>
                  <Imgix src={category.image} width={200} height={180} htmlAttributes={{ alt: category.label }} />
                </ImageBox>
                <h4>{category.label}</h4>
              </CategoryCard>
            ))}
            {CATEGORIES.map((category) => (
              <CategoryCard key={`${category.key}-duplicate`} aria-hidden='true'>
                <ImageBox>
                  <Imgix src={category.image} width={200} height={180} htmlAttributes={{ alt: `` }} />
                </ImageBox>
                <h4>{category.label}</h4>
              </CategoryCard>
            ))}
          </Track>
        </Viewport>
      </RevealOnScroll>
    </Section>
  );
}

const catsScroll = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;

const Viewport = styled.div`
  overflow: hidden;
  position: relative;
  max-width: 1100px;
  margin: 0 auto;
  -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 6%, #000 94%, transparent 100%);
  mask-image: linear-gradient(90deg, transparent 0, #000 6%, #000 94%, transparent 100%);
`;

const Track = styled.div`
  display: flex;
  gap: 18px;
  width: max-content;
  animation: ${catsScroll} 30s linear infinite;
  will-change: transform;

  ${Viewport}:hover & {
    animation-play-state: paused;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const CategoryCard = styled.div`
  background: ${palette.bgCard};
  border-radius: 14px;
  padding: 14px;
  text-align: center;
  transition: transform 0.25s ease;
  flex: 0 0 200px;

  &:hover {
    transform: translateY(-4px);
  }

  h4 {
    margin: 0;
    font-size: 17px;
    color: ${palette.cream};
  }
`;

const ImageBox = styled.div`
  height: 180px;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 12px;
  background: #f3efe3;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;
